/* ==========================================================================
   Talecraft Luxe — theme behaviour
   Vanilla JS, no dependencies. Progressive enhancement: every interaction
   below has a working no-JS fallback in the Liquid.
   ========================================================================== */
(function () {
  'use strict';

  var T = window.Talecraft || {};
  var routes = T.routes || {};

  /* --- helpers ------------------------------------------------------------ */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function formatMoney(cents) {
    var fmt = T.moneyFormat || '{{amount}}';
    var value = (cents / 100);
    function withComma(n, decimals, thousand, decimal) {
      var parts = n.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousand);
      return parts.join(decimal || '');
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_m, key) {
      switch (key) {
        case 'amount': return withComma(value, 2, ',', '.');
        case 'amount_no_decimals': return withComma(value, 0, ',', '');
        case 'amount_with_comma_separator': return withComma(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withComma(value, 0, '.', '');
        case 'amount_with_space_separator': return withComma(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return withComma(value, 0, ' ', '');
        case 'amount_with_apostrophe_separator': return withComma(value, 2, "'", '.');
        default: return withComma(value, 2, ',', '.');
      }
    });
  }

  var toastTimer;
  function toast(message) {
    var el = document.getElementById('TcToast');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    requestAnimationFrame(function () { el.classList.add('is-visible'); });
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      el.classList.remove('is-visible');
      setTimeout(function () { el.hidden = true; }, 400);
    }, 3200);
  }

  /* --- drawers ------------------------------------------------------------ */
  var openDrawer = null;
  var lastFocused = null;

  function drawerOpen(id) {
    var el = document.getElementById(id);
    if (!el) return;
    lastFocused = document.activeElement;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    openDrawer = el;
    var focusTarget = el.querySelector('[data-drawer-autofocus]') || el.querySelector('button, [href], input, select, textarea');
    if (focusTarget) setTimeout(function () { focusTarget.focus(); }, 120);
  }

  function drawerClose(el) {
    el = el || openDrawer;
    if (!el) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (openDrawer === el) openDrawer = null;
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      drawerOpen(opener.getAttribute('data-drawer-open'));
      return;
    }
    if (e.target.closest('[data-drawer-close]') || e.target.matches('.drawer__overlay')) {
      e.preventDefault();
      drawerClose(e.target.closest('.drawer'));
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openDrawer) drawerClose();
    if (e.key === 'Tab' && openDrawer) {
      var focusables = $$('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])', openDrawer)
        .filter(function (n) { return n.offsetParent !== null; });
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* --- sticky / transparent header ---------------------------------------- */
  function initHeader() {
    var wrap = $('[data-header-wrap]');
    if (!wrap) return;
    var threshold = 40;
    function onScroll() {
      wrap.classList.toggle('is-stuck', window.scrollY > threshold);
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* --- desktop nav dropdowns close on outside click ----------------------- */
  document.addEventListener('click', function (e) {
    $$('details.nav__item[open]').forEach(function (d) {
      if (!d.contains(e.target)) d.removeAttribute('open');
    });
  });

  /* --- announcement rotator ----------------------------------------------- */
  function initAnnouncement() {
    $$('[data-announcement]').forEach(function (root) {
      var slides = $$('.announcement__slide', root);
      if (slides.length < 2) return;
      var speed = parseInt(root.getAttribute('data-speed'), 10) || 5000;
      var i = 0;
      setInterval(function () {
        slides[i].classList.remove('is-active');
        i = (i + 1) % slides.length;
        slides[i].classList.add('is-active');
      }, speed);
    });
  }

  /* --- reveal on scroll ---------------------------------------------------- */
  function initReveal() {
    if (!document.body.classList.contains('motion-enabled')) return;
    if (!('IntersectionObserver' in window)) {
      $$('[data-reveal]').forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    $$('[data-reveal]').forEach(function (el) { io.observe(el); });
  }

  /* --- marquee duplication (seamless loop) -------------------------------- */
  function initMarquee() {
    $$('[data-marquee-track]').forEach(function (track) {
      if (track.dataset.duplicated) return;
      track.innerHTML += track.innerHTML;
      track.dataset.duplicated = 'true';
    });
  }

  /* --- product gallery ----------------------------------------------------- */
  function initGallery() {
    $$('[data-gallery]').forEach(function (gallery) {
      var main = $('[data-gallery-main]', gallery);
      if (!main) return;
      $$('[data-gallery-thumb]', gallery).forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var src = thumb.getAttribute('data-full');
          var alt = thumb.getAttribute('data-alt') || '';
          if (!src) return;
          main.innerHTML = '<img src="' + src + '" alt="' + alt.replace(/"/g, '&quot;') + '" width="1200" height="1500">';
          $$('[data-gallery-thumb]', gallery).forEach(function (t) { t.setAttribute('aria-current', 'false'); });
          thumb.setAttribute('aria-current', 'true');
        });
      });
    });
  }

  function setGalleryImage(gallery, mediaId) {
    if (!gallery || !mediaId) return;
    var thumb = gallery.querySelector('[data-gallery-thumb][data-media-id="' + mediaId + '"]');
    if (thumb) thumb.click();
  }

  /* --- variant picker ------------------------------------------------------ */
  function initVariants() {
    $$('[data-product-form-wrapper]').forEach(function (wrapper) {
      var dataEl = $('[data-variant-json]', wrapper);
      if (!dataEl) return;
      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (err) { return; }

      var idInput = $('[data-variant-id]', wrapper);
      var priceEl = $('[data-product-price]', wrapper);
      var submitEl = $('[data-add-to-cart]', wrapper);
      var submitText = submitEl ? submitEl.querySelector('[data-add-to-cart-text]') : null;
      var skuEl = $('[data-product-sku]', wrapper);
      var gallery = document.querySelector('[data-gallery]');

      function currentOptions() {
        return $$('[data-option-index]', wrapper)
          .filter(function (input) { return input.checked || input.tagName === 'SELECT'; })
          .map(function (input) {
            return { index: parseInt(input.getAttribute('data-option-index'), 10), value: input.value };
          })
          .sort(function (a, b) { return a.index - b.index; })
          .map(function (o) { return o.value; });
      }

      function findVariant(opts) {
        return variants.find(function (v) {
          return opts.every(function (val, i) { return v.options[i] === val; });
        });
      }

      function update() {
        var opts = currentOptions();
        var variant = findVariant(opts);

        // reflect the chosen value in the option label
        $$('[data-option-index]', wrapper).forEach(function (input) {
          if (!input.checked) return;
          var label = wrapper.querySelector('[data-option-value-for="' + input.getAttribute('data-option-index') + '"]');
          if (label) label.textContent = input.value;
        });

        if (!variant) {
          if (idInput) idInput.value = '';
          if (submitEl) { submitEl.setAttribute('disabled', 'disabled'); }
          if (submitText) submitText.textContent = T.strings.unavailable;
          return;
        }

        if (idInput) idInput.value = variant.id;
        if (priceEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            priceEl.innerHTML = '<span class="price--on-sale"><span class="price__regular">' + formatMoney(variant.compare_at_price) +
              '</span><span class="price__sale">' + formatMoney(variant.price) + '</span></span>';
          } else {
            priceEl.textContent = formatMoney(variant.price);
          }
        }
        if (skuEl) skuEl.textContent = variant.sku || '';

        if (submitEl) {
          if (variant.available) {
            submitEl.removeAttribute('disabled');
            if (submitText) submitText.textContent = T.strings.addToCart;
          } else {
            submitEl.setAttribute('disabled', 'disabled');
            if (submitText) submitText.textContent = T.strings.soldOut;
          }
        }

        if (variant.featured_media_id) setGalleryImage(gallery, String(variant.featured_media_id));

        if (window.history.replaceState && wrapper.hasAttribute('data-update-url')) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      }

      $$('[data-option-index]', wrapper).forEach(function (input) {
        input.addEventListener('change', update);
      });
      update();
    });
  }

  /* --- quantity steppers --------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    e.preventDefault();
    var wrap = btn.closest('.qty');
    var input = wrap ? wrap.querySelector('input') : null;
    if (!input) return;
    var step = btn.getAttribute('data-qty-change') === 'up' ? 1 : -1;
    var min = parseInt(input.getAttribute('min'), 10);
    if (isNaN(min)) min = 0;
    var next = Math.max(min, (parseInt(input.value, 10) || 0) + step);
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* --- cart ---------------------------------------------------------------- */
  function refreshCartDrawer() {
    return fetch(routes.root + '?section_id=cart-drawer', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-cart-drawer-contents]');
        var current = document.querySelector('[data-cart-drawer-contents]');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
      });
  }

  function refreshCartCount() {
    return fetch(routes.cart + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        $$('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
        return cart;
      });
  }

  function addToCart(form, button) {
    var formData = new FormData(form);
    if (button) {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('disabled', 'disabled');
    }
    return fetch(routes.cartAdd, {
      method: 'POST',
      headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    })
      .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
      .then(function (res) {
        if (!res.ok) {
          toast(res.data.description || res.data.message || T.strings.cartError);
          return;
        }
        return Promise.all([refreshCartCount(), refreshCartDrawer()]).then(function () {
          if (T.cartType === 'drawer') drawerOpen('CartDrawer');
          else toast(T.strings.added);
        });
      })
      .catch(function () { toast(T.strings.cartError); })
      .finally(function () {
        if (button) {
          button.removeAttribute('aria-busy');
          button.removeAttribute('disabled');
        }
      });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-product-form]');
    if (!form) return;
    e.preventDefault();
    addToCart(form, form.querySelector('[data-add-to-cart]'));
  });

  function changeLine(key, quantity) {
    return fetch(routes.cartChange, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        return Promise.all([refreshCartCount(), refreshCartDrawer()]).then(function () {
          if (document.body.classList.contains('template-cart')) window.location.reload();
        });
      })
      .catch(function () { toast(T.strings.cartError); });
  }

  document.addEventListener('click', function (e) {
    var remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      e.preventDefault();
      changeLine(remove.getAttribute('data-cart-remove'), 0);
    }
  });

  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-cart-qty]');
    if (!input) return;
    changeLine(input.getAttribute('data-cart-qty'), parseInt(input.value, 10) || 0);
  });

  /* --- quick add ----------------------------------------------------------- */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-quick-add-form]');
    if (!form) return;
    e.preventDefault();
    addToCart(form, form.querySelector('button'));
  });

  /* --- predictive search --------------------------------------------------- */
  function initSearch() {
    var input = $('[data-predictive-input]');
    var results = $('[data-predictive-results]');
    if (!input || !results || !routes.predictiveSearch) return;
    var timer;
    input.addEventListener('input', function () {
      clearTimeout(timer);
      var q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ''; return; }
      timer = setTimeout(function () {
        fetch(routes.predictiveSearch + '?q=' + encodeURIComponent(q) + '&resources[type]=product,page,article&resources[limit]=6&section_id=predictive-search')
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var fresh = doc.querySelector('[data-predictive-results]');
            results.innerHTML = fresh ? fresh.innerHTML : '';
          })
          .catch(function () { results.innerHTML = ''; });
      }, 250);
    });
  }

  /* --- collection sort ----------------------------------------------------- */
  function initSort() {
    var select = $('[data-sort-select]');
    if (!select) return;
    select.addEventListener('change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
    $$('[data-facet-form]').forEach(function (form) {
      form.addEventListener('change', function () { form.submit(); });
    });
  }

  /* --- accordions: close siblings in the same group ------------------------ */
  document.addEventListener('toggle', function (e) {
    var item = e.target;
    if (!item.matches('[data-accordion-exclusive] .accordion__item')) return;
    if (!item.open) return;
    var group = item.closest('[data-accordion-exclusive]');
    $$('.accordion__item[open]', group).forEach(function (other) {
      if (other !== item) other.removeAttribute('open');
    });
  }, true);

  /* --- image band parallax -------------------------------------------------- */
  function initParallax() {
    var bands = $$('.image-band--parallax .image-band__media');
    if (!bands.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.innerWidth < 750) return;

    bands.forEach(function (media) {
      var img = media.querySelector('img');
      if (img) img.style.height = '124%';
    });

    var ticking = false;
    function update() {
      bands.forEach(function (media) {
        var img = media.querySelector('img');
        if (!img) return;
        var rect = media.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > window.innerHeight + 200) return;
        // 0 when the band enters the viewport, 1 when it leaves the top
        var progress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
        var shift = (progress - 0.5) * (rect.height * 0.18);
        img.style.transform = 'translate3d(0,' + shift.toFixed(1) + 'px,0)';
      });
      ticking = false;
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  /* --- click-to-play video --------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var poster = e.target.closest('[data-video-play]');
    if (!poster) return;
    e.preventDefault();
    var frame = poster.closest('[data-video-frame]');
    if (!frame) return;
    var tpl = frame.querySelector('[data-video-template]');
    if (!tpl) return;
    var holder = document.createElement('div');
    holder.className = 'video-frame__embed';
    holder.innerHTML = tpl.innerHTML;
    poster.remove();
    tpl.remove();
    frame.appendChild(holder);
    var iframe = holder.querySelector('iframe');
    if (iframe) iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
  });

  /* --- pause looping videos that are off screen ----------------------------- */
  function initVideoLoops() {
    var vids = $$('.video-frame video[autoplay]');
    if (!vids.length || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var v = entry.target;
        if (entry.isIntersecting) {
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          v.pause();
        }
      });
    }, { threshold: 0.15 });
    vids.forEach(function (v) { io.observe(v); });
  }

  /* --- init ---------------------------------------------------------------- */
  function init() {
    initHeader();
    initAnnouncement();
    initReveal();
    initMarquee();
    initGallery();
    initVariants();
    initSearch();
    initSort();
    initParallax();
    initVideoLoops();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Re-run inside the theme editor when a section is re-rendered.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', function (e) {
    if (e.target.querySelector('#CartDrawer')) drawerOpen('CartDrawer');
  });
})();
